import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as TestIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import { qogitaApi } from "../../../services/api/omni/qogita";

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((err) =>
          typeof err === "string"
            ? err
            : err.msg
              ? `${err.loc?.join(".")} ${err.msg}`
              : JSON.stringify(err),
        )
        .join("; ");
    }
    return JSON.stringify(detail);
  }
  return error.message || JSON.stringify(error);
};

const QogitaCredentialsTab = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [testStatus, setTestStatus] = useState("");
  const [testingId, setTestingId] = useState(null);

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const response = await qogitaApi.listCredentials();
      setCredentials(response.data || []);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCredentials();
  }, []);

  const handleTest = async () => {
    setTestStatus("testing");
    try {
      await qogitaApi.testCredentials({
        email: form.email,
        password: form.password,
      });
      setTestStatus("success");
    } catch (err) {
      setTestStatus("error");
      setError(getErrorMessage(err));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      await qogitaApi.createCredential({
        email: form.email,
        password: form.password,
        active: true,
      });
      setSuccess("Qogita credentials saved and authenticated.");
      setDialogOpen(false);
      setForm({ email: "", password: "" });
      setTestStatus("");
      await loadCredentials();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTestStored = async (id) => {
    try {
      setTestingId(id);
      setError(null);
      const response = await qogitaApi.testStoredCredential(id);
      setSuccess(
        `Login successful for ${response.data.email}${
          response.data.expires_at
            ? ` · token expires ${new Date(response.data.expires_at).toLocaleString()}`
            : ""
        }`,
      );
      await loadCredentials();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete Qogita credentials?")) return;
    try {
      setLoading(true);
      await qogitaApi.deleteCredential(id);
      setSuccess("Credentials deleted.");
      await loadCredentials();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card sx={cp.card}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              Qogita Account
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Credentials
            </Button>
          </Box>

          {loading && credentials.length === 0 ? (
            <CircularProgress size={24} />
          ) : credentials.length === 0 ? (
            <Alert severity="info">No Qogita credentials configured yet.</Alert>
          ) : (
            <List>
              {credentials.map((cred) => (
                <ListItem
                  key={cred.id}
                  sx={cp.listItem}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        onClick={() => handleTestStored(cred.id)}
                        disabled={testingId === cred.id}
                        title="Test login"
                      >
                        {testingId === cred.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <TestIcon />
                        )}
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(cred.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={cred.email}
                    secondary={
                      <>
                        {cred.active && (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            sx={{ mr: 1 }}
                          />
                        )}
                        {cred.has_token ? "Authenticated" : "Not authenticated"}
                        {cred.expires_at &&
                          ` · expires ${new Date(cred.expires_at).toLocaleString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Qogita Credentials</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          {testStatus === "success" && (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1 }}>
              Login successful
            </Alert>
          )}
          {testStatus === "error" && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Login failed
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTest} disabled={!form.email || !form.password}>
            Test Login
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.email || !form.password || loading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QogitaCredentialsTab;
